import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/ui/SectionHeader";
import { Mail } from "lucide-react";
import { siteImages } from "@/lib/siteData";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Post = Database["public"]["Tables"]["blog_posts"]["Row"];

const isHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

// Lightweight markdown -> HTML for body content. The wrapping `.prose` provides typography.
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const inline = (s: string) =>
  escapeHtml(s)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" rel="noopener">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");

const markdownToHtml = (body: string) => {
  const blocks = body.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return blocks
    .map((raw) => {
      const block = raw.trim();
      if (!block) return "";
      const h = block.match(/^(#{1,6})\s+(.*)$/);
      if (h) {
        const level = h[1].length;
        return `<h${level}>${inline(h[2])}</h${level}>`;
      }
      if (/^(\*|-)\s+/.test(block)) {
        const items = block.split("\n").map((l) => l.replace(/^(\*|-)\s+/, "").trim()).filter(Boolean);
        return `<ul>${items.map((i) => `<li>${inline(i)}</li>`).join("")}</ul>`;
      }
      if (/^\d+\.\s+/.test(block)) {
        const items = block.split("\n").map((l) => l.replace(/^\d+\.\s+/, "").trim()).filter(Boolean);
        return `<ol>${items.map((i) => `<li>${inline(i)}</li>`).join("")}</ol>`;
      }
      if (block.startsWith(">")) {
        return `<blockquote>${inline(block.replace(/^>\s?/gm, "").trim())}</blockquote>`;
      }
      // Legacy "**Heading**" on its own line -> h2 for proper SEO hierarchy
      if (/^\*\*[^*]+\*\*$/.test(block)) {
        return `<h2>${inline(block.replace(/^\*\*|\*\*$/g, ""))}</h2>`;
      }
      return `<p>${inline(block).replace(/\n/g, "<br />")}</p>`;
    })
    .join("\n");
};

const BlogIndex = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    document.title = "Studio Talk | MyStudio Blog";
    supabase
      .from("blog_posts")
      .select("*")
      .eq("status", "published")
      .order("publish_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        setPosts(data ?? []);
        setLoading(false);
      });
  }, []);

  const cats = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach((p) => p.tags?.forEach((t) => tagSet.add(t)));
    return ["All", ...Array.from(tagSet)];
  }, [posts]);

  const filtered = activeCategory === "All" ? posts : posts.filter((p) => p.tags?.includes(activeCategory));

  return (
    <div className="pt-20">
      <Helmet>
        <title>Studio Talk | MyStudio Blog</title>
        <meta name="description" content="Photography and videography tips, behind-the-scenes stories, and studio news from MyStudio Columbus, GA." />
        <link rel="canonical" href="https://bookmystudiospace.com/blog" />
        <meta property="og:title" content="Studio Talk | MyStudio Blog" />
        <meta property="og:description" content="Photography and videography tips, behind-the-scenes stories, and studio news." />
        <meta property="og:url" content="https://bookmystudiospace.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>
      <section className="relative py-20 text-center">
        <div className="absolute inset-0 z-0">
          <img src={siteImages.heroSlider1} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-background/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">STUDIO TALK</h1>
          <p className="text-muted-foreground font-body text-lg">Articles, tips, and news from the studio</p>
          {cats.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {cats.map((c) => (
                <button key={c} onClick={() => setActiveCategory(c)} className={`px-4 py-2 text-sm font-body transition-colors ${activeCategory === c ? "bg-primary text-primary-foreground" : "bg-panel border border-border text-muted-foreground hover:text-foreground"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <p className="text-center text-muted-foreground font-mono">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground font-mono">No posts published yet. Check back soon.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-panel border border-border overflow-hidden hover:glow-amber transition-all">
                  <div className="aspect-video bg-muted overflow-hidden">
                    <img src={p.cover_image || siteImages.heroSlider1} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-5">
                    {p.tags?.[0] && <span className="inline-block bg-primary/10 text-primary text-xs px-3 py-1 font-mono mb-3">{p.tags[0]}</span>}
                    <h3 className="text-lg font-display tracking-wider mb-2 group-hover:text-primary transition-colors">{p.title}</h3>
                    {p.excerpt && <p className="text-sm text-muted-foreground font-body mb-3 line-clamp-2">{p.excerpt}</p>}
                    <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                      <span>{p.publish_at ? new Date(p.publish_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}</span>
                      {p.read_minutes && <span>{p.read_minutes} min read</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    supabase
      .from("blog_posts")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle()
      .then(({ data }) => {
        setPost(data);
        setLoading(false);
        if (data) {
          document.title = `${data.seo_title || data.title} | MyStudio`;
          supabase
            .from("blog_posts")
            .select("*")
            .eq("status", "published")
            .neq("id", data.id)
            .limit(3)
            .then(({ data: r }) => setRelated(r ?? []));
        }
      });
  }, [slug]);

  if (loading) return <div className="pt-32 text-center text-muted-foreground font-mono">Loading…</div>;
  if (!post) return <div className="pt-32 text-center text-muted-foreground">Post not found</div>;

  return (
    <div className="pt-20">
      <Helmet>
        <title>{`${post.seo_title || post.title} | MyStudio`}</title>
        <meta name="description" content={post.seo_description || post.excerpt || post.title} />
        <link rel="canonical" href={`https://bookmystudiospace.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.seo_title || post.title} />
        <meta property="og:description" content={post.seo_description || post.excerpt || post.title} />
        <meta property="og:url" content={`https://bookmystudiospace.com/blog/${post.slug}`} />
        {post.cover_image && <meta property="og:image" content={post.cover_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          image: post.cover_image ? [post.cover_image] : undefined,
          datePublished: post.publish_at || post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { "@type": "Organization", name: "MyStudio" },
          publisher: { "@type": "Organization", name: "MyStudio" },
          mainEntityOfPage: `https://bookmystudiospace.com/blog/${post.slug}`,
        })}</script>
      </Helmet>
      <header className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40 z-10" />
        <img
          src={post.cover_image || siteImages.heroSlider1}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 z-20 container mx-auto px-4 pb-12">
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/blog" className="hover:text-primary transition-colors">Studio Talk</Link></li>
            </ol>
          </nav>
          {post.tags?.[0] && (
            <span className="inline-block bg-primary/10 text-primary text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 font-mono mb-5 border border-primary/30">
              {post.tags[0]}
            </span>
          )}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-display font-extrabold uppercase tracking-wider leading-[1.1] max-w-4xl">
            {post.title}
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 max-w-3xl py-6 border-b border-border mb-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-[0.2em]">
            {(post.publish_at ? new Date(post.publish_at) : new Date(post.created_at)).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            {post.read_minutes ? ` · ${post.read_minutes} min read` : ""}
          </p>
          {post.excerpt && (
            <p className="text-xs text-muted-foreground font-mono">By MyStudio Editorial</p>
          )}
        </div>
        {post.excerpt && (
          <p className="mt-4 text-lg md:text-xl text-foreground/80 font-body leading-relaxed italic">
            {post.excerpt}
          </p>
        )}
      </div>
      <article className="container mx-auto px-4 max-w-3xl pb-20">
        <div className="prose prose-invert prose-lg max-w-none font-body">
          {post.body && (
            <div
              dangerouslySetInnerHTML={{
                __html: isHtml(post.body) ? post.body : markdownToHtml(post.body),
              }}
            />
          )}
        </div>

        {related.length > 0 && (
          <div className="mt-16 pt-8 border-t border-border">
            <SectionHeader title="RELATED POSTS" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-panel border border-border overflow-hidden hover:glow-amber transition-all">
                  <div className="aspect-video bg-muted overflow-hidden">
                    <img src={p.cover_image || siteImages.heroSlider1} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-display tracking-wider text-sm group-hover:text-primary transition-colors">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 p-8 bg-primary/5 border border-primary/20 text-center">
          <Mail className="w-8 h-8 text-primary mx-auto mb-3" />
          <h3 className="font-display tracking-widest text-xl mb-2">ENJOYED THIS?</h3>
          <p className="text-muted-foreground font-body text-sm mb-4">Join the MyStudio community for more tips and studio updates.</p>
          <Link to="/#subscribe">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-6">Join the Community</Button>
          </Link>
        </div>
      </article>
    </div>
  );
};

export { BlogIndex, BlogPost };
